import React, { forwardRef, type KeyboardEvent, type ReactNode } from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { borderRadius, layoutGap } from '../../../theme/theme';
import { getWorkflowWorkspacePalette, type WorkspaceTone } from './WorkflowWorkspacePrimitives';

export interface WorkspaceSelectableCardProps {
  selected: boolean;
  tone?: WorkspaceTone;
  onSelect: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  children: ReactNode;
  action?: ReactNode;
  scrollMarginBlock?: number;
  dataTestId?: string;
  ariaSelected?: boolean;
  role?: string;
}

const LEFT_ACCENT_WIDTH = 4;

const getToneColor = (theme: Theme, tone: WorkspaceTone): string => {
  switch (tone) {
    case 'accent':
      return theme.palette.primary.main;
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    case 'info':
      return theme.palette.info.main;
    default:
      return theme.palette.text.secondary;
  }
};

const WorkspaceSelectableCard = forwardRef<HTMLDivElement, WorkspaceSelectableCardProps>(
  function WorkspaceSelectableCard(
    {
      selected,
      tone = 'neutral',
      onSelect,
      onKeyDown,
      children,
      action,
      scrollMarginBlock,
      dataTestId,
      ariaSelected,
      role,
    },
    ref
  ) {
    const theme = useTheme();
    const palette = getWorkflowWorkspacePalette(theme);
    const toneColor = getToneColor(theme, tone);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);

      if (event.key === 'Enter' || event.key === ' ') {
        if (!event.defaultPrevented) {
          event.preventDefault();
          onSelect();
        }
      }
    };

    return (
      <Box
        ref={ref}
        data-testid={dataTestId}
      role={role}
      aria-selected={ariaSelected ?? selected}
      tabIndex={selected ? 0 : -1}
      onClick={(event) => {
        onSelect();
        event.currentTarget.focus();
      }}
      onKeyDown={handleKeyDown}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'flex-start' },
        justifyContent: 'space-between',
        gap: { xs: layoutGap.comfort, md: layoutGap.rise },
        px: selected ? 1.35 : 1.2,
        py: selected ? 1.15 : 1.05,
        minHeight: 80,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${
          selected
            ? alpha(toneColor, 0.36)
            : alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.22 : 0.16)
        }`,
        backgroundColor: selected
          ? alpha(toneColor, theme.palette.mode === 'light' ? 0.05 : 0.1)
          : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.9 : 0.24),
        boxShadow: selected
          ? `inset ${LEFT_ACCENT_WIDTH}px 0 0 ${toneColor}, 0 1px 2px ${alpha(
              theme.palette.common.black,
              0.04
            )}`
          : `0 1px 1px ${alpha(theme.palette.common.black, 0.025)}`,
        cursor: 'pointer',
        scrollMarginBlock: scrollMarginBlock ?? 12,
        transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          borderColor: selected
            ? alpha(toneColor, 0.48)
            : alpha(theme.palette.text.primary, 0.18),
          backgroundColor: selected
            ? alpha(toneColor, theme.palette.mode === 'light' ? 0.075 : 0.13)
            : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.96 : 0.3),
        },
        '&:hover .workspace-card-action': {
          opacity: 1,
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
      {action ? (
        <Box
          className="workspace-card-action"
          sx={{
            flexShrink: 0,
            opacity: selected ? 1 : 0,
            transition: 'opacity 0.15s ease',
          }}
        >
          {action}
        </Box>
      ) : null}
    </Box>
  );
});

export default WorkspaceSelectableCard;
